import { ClockCircleOutlined, CommentOutlined } from '@ant-design/icons';
import { Flex, Tag, Typography } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import type { UnresolvedVendorErrorCodeResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { TimestampCards } from '~/app/components/errorManagement/TimestampCards/TimestampCards';
import { renderOccurrenceCountCell } from '~/app/components/errorManagement/tableCellRenderers';
import { statusTagColor } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/utils';
import {
  ViewDrawerSectionHeader,
  type ViewDrawerSectionTone,
} from '~/app/components/errorManagement/ViewDrawerSectionHeader/ViewDrawerSectionHeader';

const SECTION_TONES = {
  remark: {
    accent: 'var(--evimo-warning)',
    bg: 'color-mix(in srgb, var(--evimo-warning) 10%, transparent)',
    icon: <CommentOutlined />,
  },
  observation: {
    accent: 'var(--evimo-blue)',
    bg: 'color-mix(in srgb, var(--evimo-blue) 8%, transparent)',
    icon: <ClockCircleOutlined />,
  },
} as const satisfies Record<string, ViewDrawerSectionTone>;

const METRIC_CARD_STYLE: CSSProperties = {
  width: 112,
  padding: '10px 8px',
  borderRadius: 10,
  background: 'color-mix(in srgb, var(--evimo-blue) 5%, transparent)',
  border: '1px solid color-mix(in srgb, var(--evimo-blue) 14%, transparent)',
  textAlign: 'center',
};

function MetricCard({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div style={METRIC_CARD_STYLE}>
      <Typography.Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 11,
          marginBottom: 6,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography.Text>
      <Flex justify="center" align="center" style={{ minHeight: 28 }}>
        {children}
      </Flex>
    </div>
  );
}

export interface UnresolvedObservationSectionProps {
  record: UnresolvedVendorErrorCodeResponseDto;
}

export function UnresolvedObservationSection({
  record,
}: UnresolvedObservationSectionProps) {
  const { t } = useTranslation('errorManagement');

  return (
    <>
      <ViewDrawerSectionHeader
        tone={SECTION_TONES.remark}
        label={t('unresolved.viewDrawer.sections.remark')}
      />
      <div
        style={{
          marginBottom: 24,
          padding: '16px 18px',
          borderRadius: 12,
          background: SECTION_TONES.remark.bg,
          border: `1px solid color-mix(in srgb, ${SECTION_TONES.remark.accent} 28%, transparent)`,
          borderLeft: `4px solid ${SECTION_TONES.remark.accent}`,
        }}
      >
        <Typography.Paragraph
          style={{
            margin: 0,
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--evimo-text, #1a1d23)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {record.remark?.trim()
            ? record.remark
            : t('unresolved.viewDrawer.noRemark')}
        </Typography.Paragraph>
      </div>

      <ViewDrawerSectionHeader
        tone={SECTION_TONES.observation}
        label={t('unresolved.viewDrawer.sections.observation')}
      />
      <Flex gap={8} justify="center" wrap="wrap" style={{ marginBottom: 12 }}>
        <MetricCard label={t('unresolved.columns.status')}>
          <Tag
            color={statusTagColor(record.status)}
            style={{ marginInlineEnd: 0 }}
          >
            {t(`unresolved.status.${record.status}`)}
          </Tag>
        </MetricCard>
        <MetricCard label={t('unresolved.columns.occurrenceCount')}>
          {renderOccurrenceCountCell(record.occurrenceCount)}
        </MetricCard>
        <MetricCard label={t('unresolved.columns.version')}>
          <Typography.Text
            strong
            style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14 }}
          >
            v{record.version}
          </Typography.Text>
        </MetricCard>
      </Flex>
      <TimestampCards
        items={[
          {
            label: t('unresolved.columns.firstSeenAt'),
            value: record.firstSeenAt,
          },
          {
            label: t('unresolved.columns.lastSeenAt'),
            value: record.lastSeenAt,
          },
        ]}
      />
    </>
  );
}
